import type { IPaymentProfile } from "models/HaulPay";
import type { PreviousMetadata } from "features/main/HaulPay/Shared/nonFactoredToFactoredFlow";

export type NonFactoredBillOutToPayCarrierResult = {
  carrier: Partial<IPaymentProfile>;
  carrierInvoiceAmount: number;
};

const MODAL_ID = "non-factored-bill-out-to-pay-carrier";

const createAbortError = () => {
  const error = new Error("The operation was aborted.");
  error.name = "AbortError";

  return error;
};

interface ConfirmNonFactoredBillOutToPayCarrierArgs {
  openModal: (modalId: string, props: Record<string, unknown>) => void;
  closeModal?: (modalId: string) => void;
  fromContractType?: string;
  toContractType?: string;
  previousMetadata?: PreviousMetadata;
  signal?: AbortSignal;
}

export const confirmNonFactoredBillOutToPayCarrier = ({
  openModal,
  closeModal,
  fromContractType,
  toContractType,
  previousMetadata,
  signal
}: ConfirmNonFactoredBillOutToPayCarrierArgs) =>
  new Promise<NonFactoredBillOutToPayCarrierResult>((resolve, reject) => {
    if (signal?.aborted) {
      reject(createAbortError());
      return;
    }

    let isSettled = false;

    const cleanupAbortListener = () => {
      if (signal && handleAbort) {
        signal.removeEventListener("abort", handleAbort);
      }
    };

    const settleResolve = (value: NonFactoredBillOutToPayCarrierResult) => {
      if (isSettled) return;

      isSettled = true;
      cleanupAbortListener();
      resolve(value);
    };

    const settleReject = (reason: unknown) => {
      if (isSettled) return;

      isSettled = true;
      cleanupAbortListener();
      reject(reason);
    };

    const handleAbort = () => {
      closeModal?.(MODAL_ID);
      settleReject(createAbortError());
    };

    signal?.addEventListener("abort", handleAbort, { once: true });

    openModal(MODAL_ID, {
      fromContractType,
      toContractType,
      previousMetadata,
      onConfirm: (result: NonFactoredBillOutToPayCarrierResult) => {
        if (isSettled || signal?.aborted) {
          return;
        }

        settleResolve(result);
      },
      onCloseModal: () => {
        if (isSettled || signal?.aborted) {
          return;
        }

        settleReject("cancel-edit-fr");
      }
    });
  });
